/**
 * SFarm Monitor - Device Caching Layer
 * Implements stale-while-revalidate pattern for API data
 */

const CACHE_PREFIX = 'sf_cache:';

// TTL values in milliseconds
const TTL = {
  FIELDS: 30 * 60 * 1000,        // 30 minutes
  FIELD_POLYGONS: 30 * 60 * 1000, // 30 minutes
  GEO_ALERTS: 5 * 60 * 1000,      // 5 minutes
  ENGINE_ALERTS: 5 * 60 * 1000,   // 5 minutes (was 2-5 min)
  INVITES: 5 * 60 * 1000,         // 5 minutes
  WEATHER: 10 * 60 * 1000,        // 10 minutes
  CASES: 5 * 60 * 1000,           // 5 minutes
};

/**
 * Generate cache key
 */
const getCacheKey = (type, ...args) => {
  return `${CACHE_PREFIX}${type}:${args.join(':')}`;
};

/**
 * Get data from cache
 */
export const getFromCache = (type, ...args) => {
  try {
    const key = getCacheKey(type, ...args);
    const cached = localStorage.getItem(key);
    
    if (!cached) return null;
    
    const { data, timestamp, ttl } = JSON.parse(cached);
    const isExpired = Date.now() - timestamp > ttl;
    
    return {
      data,
      isExpired,
      timestamp,
      age: Date.now() - timestamp
    };
  } catch (e) {
    console.warn('[Cache] Read error:', e);
    return null;
  }
};

/**
 * Set data to cache
 */
export const setToCache = (type, args, data, customTTL) => {
  try {
    const key = getCacheKey(type, ...args);
    const ttl = customTTL || TTL[type.toUpperCase()] || 5 * 60 * 1000;
    
    const cacheData = {
      data,
      timestamp: Date.now(),
      ttl,
      version: generateHash(data)
    };
    
    localStorage.setItem(key, JSON.stringify(cacheData));
    return true;
  } catch (e) {
    console.warn('[Cache] Write error:', e);
    return false;
  }
};

/**
 * Clear specific cache
 */
export const clearCache = (type, ...args) => {
  try {
    const key = getCacheKey(type, ...args);
    localStorage.removeItem(key);
  } catch (e) {
    console.warn('[Cache] Clear error:', e);
  }
};

/**
 * Clear all cache for a prefix
 */
export const clearCacheByPrefix = (prefix) => {
  try {
    const fullPrefix = `${CACHE_PREFIX}${prefix}`;
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(fullPrefix)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (e) {
    console.warn('[Cache] Clear prefix error:', e);
  }
};

/**
 * Clear all SFarm cache
 */
export const clearAllCache = () => {
  try {
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (e) {
    console.warn('[Cache] Clear all error:', e);
  }
};

/**
 * Generate simple hash for data comparison
 */
const generateHash = (data) => {
  const str = JSON.stringify(data);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString(36);
};

/**
 * Check if data has changed
 */
export const hasDataChanged = (type, args, newData) => {
  const cached = getFromCache(type, ...args);
  if (!cached) return true;
  
  const cachedVersion = JSON.parse(localStorage.getItem(getCacheKey(type, ...args)))?.version;
  const newVersion = generateHash(newData);
  
  return cachedVersion !== newVersion;
};

// ============================================
// SPECIALIZED CACHE FUNCTIONS
// ============================================

/**
 * Cache farm fields with polygon data
 */
export const cacheFields = (farmId, fields) => {
  setToCache('fields', [farmId], fields);
  
  // Also cache each field's polygon separately
  fields.forEach(field => {
    if (field.polygon) {
      setToCache('field_polygons', [field.id], field.polygon);
    }
  });
};

export const getCachedFields = (farmId) => {
  return getFromCache('fields', farmId);
};

/**
 * Cache weather data
 */
export const cacheWeather = (farmId, weather) => {
  setToCache('weather', [farmId], weather);
};

export const getCachedWeather = (farmId) => {
  return getFromCache('weather', farmId);
};

/**
 * Cache alerts (geo and engine)
 */
export const cacheGeoAlerts = (farmId, alerts) => {
  setToCache('geo_alerts', [farmId], alerts);
};

export const getCachedGeoAlerts = (farmId) => {
  return getFromCache('geo_alerts', farmId);
};

export const cacheEngineAlerts = (fieldId, alerts) => {
  setToCache('engine_alerts', [fieldId], alerts);
};

export const getCachedEngineAlerts = (fieldId) => {
  return getFromCache('engine_alerts', fieldId);
};

/**
 * Cache agronomist invites
 */
export const cacheInvites = (agronomistId, invites) => {
  setToCache('invites', [agronomistId], invites);
};

export const getCachedInvites = (agronomistId) => {
  return getFromCache('invites', agronomistId);
};

/**
 * Cache cases
 */
export const cacheCases = (userId, cases) => {
  setToCache('cases', [userId], cases);
};

export const getCachedCases = (userId) => {
  return getFromCache('cases', userId);
};

// ============================================
// STALE-WHILE-REVALIDATE HOOK HELPERS
// ============================================

/**
 * Create a fetch function with caching
 * Returns cached data immediately if available, then revalidates
 */
export const createCachedFetch = (type, fetchFn, cacheArgs) => {
  return async () => {
    const cached = getFromCache(type, ...cacheArgs);
    
    // Return cached data immediately if available
    const cachedData = cached?.data || null;
    const needsRevalidation = !cached || cached.isExpired;
    
    // If we have valid cached data and it's not expired, just return it
    if (cachedData && !needsRevalidation) {
      return { data: cachedData, fromCache: true, revalidating: false };
    }
    
    // Otherwise, fetch fresh data
    try {
      const freshData = await fetchFn();
      setToCache(type, cacheArgs, freshData);
      
      return { 
        data: freshData, 
        fromCache: false, 
        revalidating: false,
        dataChanged: hasDataChanged(type, cacheArgs, freshData)
      };
    } catch (error) {
      // On error, return cached data if available
      if (cachedData) {
        return { data: cachedData, fromCache: true, error, revalidating: false };
      }
      throw error;
    }
  };
};

export default {
  getFromCache,
  setToCache,
  clearCache,
  clearCacheByPrefix,
  clearAllCache,
  hasDataChanged,
  cacheFields,
  getCachedFields,
  cacheWeather,
  getCachedWeather,
  cacheGeoAlerts,
  getCachedGeoAlerts,
  cacheEngineAlerts,
  getCachedEngineAlerts,
  cacheInvites,
  getCachedInvites,
  cacheCases,
  getCachedCases,
  createCachedFetch,
  TTL
};
